// src/steps/Step1OrgGoal.js
import { AssistantMark, AssistantNote } from "../components/common/AssistantFields";
import React, { useState } from "react";
import { Box, Typography, Tooltip, IconButton, FormControlLabel, Checkbox, Paper } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import WordCountTextFieldWithExample from "../components/WordCountTextFieldWithExample";

// Example text (matches the structured template from fillDummyValues)
const ORG_GOAL_EXAMPLE = `
1) Context: We want to improve the water quality in our city - the LDT should help us to identify the high risk areas.
2) Main challenge: We don't have clean data, we need a better infrastructure, we want to involve the climate department but don't know how. We are also missing creative participation methods to make the public accept changes in the city.
3) Capacity-building goal: Data handling, creative participatory methods, inter-departmental communications skills, interoperability solution management.
4) Desired change: We can scale it up to other departments; the water quality achieved desired standards, the inter-departmental work is making a lot of fun.
5) Success measuring: Water quality itself has risen, the survey showed that the public is really happy about the new available drinking spots in the city center. The municipality rethinks how it usually operates and tries to change it.
`.trim();

// Guiding template text shown in the “Show guiding questions” panel
const ORG_GOAL_GUIDING_TEXT = `
1) Context: What LDT use case are you working on? Describe it.
2) Main challenge: What is missing or not working? What skills are you missing?
3) Capacity-building goal: What should staff learn to make the use case running?
4) Desired change: Imagine that your use case is working as planned. What can you achieve now?
5) Success measuring: How would you measure the impact of your use case for organisational change (1–2 signals)?
`.trim();

export default function Step1OrgGoal({
  orgGoal,
  onChangeOrgGoal,
  certificationNeeded,
  onChangeCertificationNeeded,
  onSave,
  sessionId,
  onValidityChange, // from App.js
}) {
  const [isValid, setIsValid] = useState(false);

  // Local + lifted validity update
  const handleValidityChange = (valid) => {
    setIsValid(valid);
    if (onValidityChange) {
      onValidityChange(valid);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          backgroundColor: "rgba(0, 0, 0, 0.02)",
          mb: 3,
        }}
      >
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ mr: 1 }}>
            Step 1 · LDT Use Case Goals
          </Typography>
          <Tooltip
            title={
              <Typography sx={{ fontSize: 16, lineHeight: 1.5 }}>
                Summarise what your organisation wants to achieve with this
                learning journey. Focus on the context, main challenge,
                capacity-building goal, desired change, and how you would
                recognise success.
              </Typography>
            }
          >
            <IconButton size="small" sx={{ color: "black", p: 0.5 }}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          LDT use case goals for this learning journey
        </Typography>

        <AssistantMark field="org_goal">

        <WordCountTextFieldWithExample
          // label removed to avoid redundancy with the section header
          value={orgGoal}
          onChange={onChangeOrgGoal}
          minRows={10}
          minWords={50}
          maxWords={100}
          warnOverBy={10}
          maxCharsPerWord={60}
          onValidityChange={handleValidityChange}
          exampleText={ORG_GOAL_EXAMPLE}
          exampleTitle="Example LDT use case goals"
          guidingText={ORG_GOAL_GUIDING_TEXT}
          guidingTitle="Template to describe the LDT use case goals"
          placeholder={
            "Click on the \"Show guiding questions\" button to see a template for structuring your answer."
          }
        />

        </AssistantMark>
      </Box>

      <AssistantNote field="certification_needed" />
      <Paper
        elevation={certificationNeeded ? 3 : 1}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          border: "2px solid",
          borderColor: certificationNeeded ? "primary.main" : "divider",
          backgroundColor: certificationNeeded ? "primary.50" : "background.paper",
          transition: "all 0.2s ease",
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box sx={{ mt: 0.5, color: certificationNeeded ? "primary.main" : "text.secondary" }}>
          <WorkspacePremiumIcon fontSize="large" />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={certificationNeeded}
                onChange={(e) => onChangeCertificationNeeded(e.target.checked)}
                color="primary"
                size="large"
                sx={{ ml: -1 }} // slightly offset the checkbox to align with title text
              />
            }
            label={
              <Typography variant="h6" fontWeight="600" color={certificationNeeded ? "primary.main" : "text.primary"}>
                T4R Certification Pathway
              </Typography>
            }
            sx={{ m: 0 }}
          />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
            Check this box if the learner(s) intend(s) to be formally certified. When enabled, specific MLUs (e.g., EDU-101 and EDU-102) will be <strong>automatically grouped together in pairs</strong> to fulfill certification requirements.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
